import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { connectionsService } from '../api/connectionsService';
import type { ConnectionListItem } from '../api/types';
import { useAuth } from './AuthContext';

interface ConnectionsContextType {
  connections: ConnectionListItem[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  fetchConnections: (page?: number, limit?: number) => Promise<void>;
  setPage: (page: number) => void;
  deleteConnection: (id: string) => Promise<void>;
  refreshConnections: () => Promise<void>;
}

const ConnectionsContext = createContext<ConnectionsContextType | undefined>(undefined);
ConnectionsContext.displayName = 'ConnectionsContext';

const DEFAULT_LIMIT = 20;

export const ConnectionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [connections, setConnections] = useState<ConnectionListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const fetchConnections = useCallback(async (fetchPage: number = 1, fetchLimit: number = DEFAULT_LIMIT) => {
    setLoading(true);
    setError(null);
    try {
      const response = await connectionsService.getUserConnections(fetchPage, fetchLimit);
      setConnections(response.connections);
      setTotal(response.total);
      setPageState(fetchPage);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch connections';
      setError(message);
      console.error('Error fetching connections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const setPage = useCallback((newPage: number) => {
    fetchConnections(newPage, limit);
  }, [fetchConnections, limit]);

  const deleteConnection = useCallback(async (id: string) => {
    try {
      await connectionsService.deleteConnection(id);
      setConnections(prev => prev.filter(conn => conn.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete connection';
      setError(message);
      console.error('Error deleting connection:', err);
      throw err;
    }
  }, []);

  const refreshConnections = useCallback(async () => {
    await fetchConnections(page, limit);
  }, [fetchConnections, page, limit]);

  // Load connections when authenticated (and re-fetch on login)
  useEffect(() => {
    if (isAuthenticated) {
      fetchConnections(1, limit);
    } else {
      setConnections([]);
      setTotal(0);
    }
  }, [isAuthenticated]);

  const value = useMemo<ConnectionsContextType>(() => ({
    connections,
    loading,
    error,
    total,
    page,
    limit,
    totalPages,
    fetchConnections,
    setPage,
    deleteConnection,
    refreshConnections,
  }), [connections, loading, error, total, page, limit, totalPages, fetchConnections, setPage, deleteConnection, refreshConnections]);

  return (
    <ConnectionsContext.Provider value={value}>
      {children}
    </ConnectionsContext.Provider>
  );
};

export const useConnections = (): ConnectionsContextType => {
  const context = useContext(ConnectionsContext);
  if (context === undefined) {
    throw new Error('useConnections must be used within a ConnectionsProvider');
  }
  return context;
};